export type BasketBindingApiKey = undefined | string | Promise<string>;

export type WidgetBasketEvent =
  | 'basketDeleted'
  | 'basketProductChanged'
  | 'orderCreated';

export enum WidgetBindingPlace {
  PRODUCT_CARD = 'PRODUCT_CARD',
  BASKET_SUMMARY = 'BASKET_SUMMARY',
  ORDER_CREATE = 'ORDER_CREATE',
  BASKET_POPUP = 'BASKET_POPUP',
  THANK_YOU_PAGE = 'THANK_YOU_PAGE',
  CHECKOUT_PAGE = 'CHECKOUT_PAGE',
  MINICART_PAGE = 'MINICART_PAGE',
  REGISTERFORM_PAGE = 'REGISTERFORM_PAGE',
  LOGIN_PAGE = 'LOGIN_PAGE',
}

export type WidgetBasketEventHandler = (
  event: WidgetBasketEvent
) => boolean | Promise<boolean>;

export interface WidgetInitOptions {
  /**
   * Merchant Client Id.
   */
  merchantClientId: string;
  /**
   * Unique basket binding API key - it's obtained from InpostPay API.
   * If it's known at the page render: can be provided as string.
   * If it can be resolved during page load: can be a promise.
   * Otherwise: 'undefined', widget will assume that basket is not bound, because it can't check basket binding status without knowing api key.
   * And in such case, it's mandatory for 'unboundWidgetClicked' to return a 'apiKey', otherwise baskets can't be bound.
   */
  basketBindingApiKey: BasketBindingApiKey;
  /**
   * This callback function is mandatory for unbound basket and for binding_place=PRODUCT_CARD.
   * When the promise is rejected with Error('UNDELIVERABLE_PRODUCT') widget won't show error popup or binding popup.
   * It'll be called to add the product to merchant's basket when user clicks on the widget.
   * @return promise with 'basketBindingApiKey'
   */
  unboundWidgetClicked?: (productId?: string) => Promise<string>;
  /**
   * Callback function to reflect basket updates on merchant's page.
   * If not provided or returning false - widget will refresh the entire page.
   * @return 'true' if widget should not refresh the page.
   */
  handleBasketEvent?: WidgetBasketEventHandler;
  /**
   * Widget-API base url used for frontend to API communication.
   * By default, https://inpostpay-widget-api.inpost.pl is used.
   */
  apiBaseUrl?: string;
  /**
   * If set to true BrowserId will be persisted and read from localStorage.
   * If set to true the app will always try to open a https deep link, if false the deep link url
   * will be deducted based on the UserAgent.
   * Default: false
   */
  webView?: boolean;
  /**
   * Custom language. By default, widget will use browser's language and 'pl' as fallback
   */
  language?: string;
}

export interface InPostPayWidget {
  refresh: (options?: Partial<WidgetInitOptions>) => void;
}

export interface InPostPayWidgetNamespace {
  init: (options: WidgetInitOptions) => InPostPayWidget;
}

declare global {
  export interface Window {
    InPostPayWidget: InPostPayWidgetNamespace;
    __INPOST_PAY_WIDGET__?: InPostPayWidget;
    FINESIO_INPOST_PAY_WIDGET?: InPostPayWidget;
  }
  namespace JSX {
    interface IntrinsicElements {
      'inpost-thank-you': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

declare function initialiseInpostPayWidget(
  options: WidgetInitOptions
): InPostPayWidget;
